import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { IResolveRow, ResolveChoice, allCurrent, initialChoice } from '../rules/item-write-resolve.rules';
import * as copy from '../rules/item-write-messages';

/**
 * The resolve popup of a write conflict (HUBX-8466; design "Listing — shared write rules",
 * Block B). One per surface - the listing row and the Edit modal - with the same rows and
 * the same answers.
 *
 * It shows what the surface hands it and answers with the user's choice per member. It reads
 * nothing and writes nothing: the surface re-reads the item (rows null while it does), and
 * applies the answer to its store or its form.
 *
 * Discard my changes answers at once, every row Current - the edit goes, as with Undo; Apply
 * answers with the choice made row by row; Cancel and the X leave the conflict open. There is
 * no "keep all mine": the rows include members the user never touched, whose value on screen
 * is the stale one, and one click would put all of them back over the other writer.
 */
@Component({
  selector: 'app-conflict-resolve',
  templateUrl: './conflict-resolve.component.html',
  styleUrls: ['./conflict-resolve.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor]
})
export class ConflictResolveComponent implements OnChanges {
  /** The item's Part Number, in the title. */
  @Input() partNumber = '';
  /** The members that differ; null while the item is re-read. */
  @Input() rows: IResolveRow[] | null = null;
  /** A failed re-read: the popup offers a retry. */
  @Input() error: string | null = null;
  /** Opened over the Edit modal: it sits above it. */
  @Input() overModal = false;

  @Output() resolved = new EventEmitter<Map<string, ResolveChoice>>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();

  readonly copy = copy;
  choices = new Map<string, ResolveChoice>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      this.choices = new Map((this.rows ?? []).map(row => [row.key, initialChoice(row)] as [string, ResolveChoice]));
    }
  }

  get ready(): boolean {
    return !!this.rows && !this.error;
  }

  isChosen(row: IResolveRow, choice: ResolveChoice): boolean {
    return this.choices.get(row.key) === choice;
  }

  choose(row: IResolveRow, choice: ResolveChoice): void {
    if (choice === 'yours' && !row.writable) { return; }
    this.choices.set(row.key, choice);
  }

  apply(): void {
    if (!this.ready) { return; }
    this.resolved.emit(new Map(this.choices));
  }

  discardChanges(): void {
    if (!this.ready) { return; }
    this.resolved.emit(allCurrent(this.rows as IResolveRow[]));
  }

  trackByKey(_: number, row: IResolveRow): string {
    return row.key;
  }
}
